import { useState, useEffect } from 'react';
import { TextField, Button, Alert, CircularProgress, InputAdornment, IconButton } from '@mui/material';
import { Eye, EyeOff, CheckCircle2, ShieldAlert } from 'lucide-react';
import { api, clearToken } from './api';
import './login.css';

const inputSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: 2.5,
    backgroundColor: '#FAFAFB',
    '& fieldset': { borderColor: '#E5E7EB' },
    '&:hover fieldset': { borderColor: '#D0D5DD' },
    '&.Mui-focused': { backgroundColor: '#fff', boxShadow: '0 0 0 4px rgba(var(--primary-rgb),0.10)' },
    '&.Mui-focused fieldset': { borderColor: 'var(--primary)', borderWidth: 1.5 },
  },
};
const btnSx = {
  borderRadius: 2.5, py: 1.35, textTransform: 'none', fontSize: 15, fontWeight: 700,
  background: 'linear-gradient(135deg,var(--primary) 0%,var(--primary-dark) 100%)',
  boxShadow: '0 12px 26px rgba(var(--primary-rgb),0.32)',
  '&:hover': { background: 'linear-gradient(135deg,var(--primary) 0%,var(--primary-darkest) 100%)' },
  '&.Mui-disabled': { background: '#E5E7EB', color: '#9DA4AE', boxShadow: 'none' },
};
const wrap = {
  minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
  background: '#F5F7FA', padding: 24,
  fontFamily: "'Plus Jakarta Sans','Inter',sans-serif",
};
const card = {
  width: '100%', maxWidth: 440, background: '#fff', borderRadius: 18, padding: 40,
  boxShadow: '0 10px 40px rgba(16,24,40,0.10), 0 0 0 1px rgba(16,24,40,0.03)',
  borderTop: '3px solid var(--primary)',
};
const goLogin = () => { clearToken(); window.history.pushState(null, '', '/login'); window.location.reload(); };

export default function SetPassword() {
  const params = new URLSearchParams(window.location.search);
  const token = params.get('token') || '';
  const isReset = window.location.pathname.startsWith('/reset');
  const [state, setState] = useState('checking'); // checking | invalid | form | done
  const [info, setInfo] = useState(null);
  const [pw, setPw] = useState('');
  const [confirm, setConfirm] = useState('');
  const [show, setShow] = useState(false);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!token) { setState('invalid'); return; }
    let active = true;
    api.checkResetToken(token)
      .then((d) => { if (active) { if (d.valid) { setInfo(d); setState('form'); } else setState('invalid'); } })
      .catch(() => active && setState('invalid'));
    return () => { active = false; };
  }, [token]);

  const submit = async (e) => {
    e?.preventDefault();
    setErr('');
    if (pw.length < 8) { setErr('Password must be at least 8 characters.'); return; }
    if (pw !== confirm) { setErr('The two passwords do not match.'); return; }
    setBusy(true);
    try {
      await api.setPassword(token, pw);
      setState('done');
    } catch (ex) {
      setErr(ex.message || 'Could not set your password.');
      setBusy(false);
    }
  };

  const title = isReset ? 'Reset your password' : 'Set your password';

  return (
    <div style={wrap}>
      <div style={card} className="fade-up">
        <img src="/assets/augment-logo/augment-hr-primary.svg" alt="Augment HR" style={{ width: 184, display: 'block', marginBottom: 28 }} />

        {state === 'checking' && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, color: '#667085' }}>
            <CircularProgress size={20} /> Checking your link…
          </div>
        )}

        {state === 'invalid' && (
          <>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
              <ShieldAlert size={22} color="#F04438" />
              <h1 style={{ fontSize: 22, fontWeight: 800, color: '#0B1220', margin: 0 }}>Link expired or invalid</h1>
            </div>
            <p style={{ color: '#667085', fontSize: 15, lineHeight: 1.6, marginTop: 0 }}>
              This {isReset ? 'reset' : 'activation'} link has expired or has already been used.
              {isReset ? ' Please request a new one from the login page.' : ' Please ask your HR/admin to resend the invite.'}
            </p>
            <Button fullWidth variant="contained" size="large" disableElevation sx={btnSx} onClick={goLogin}>
              Back to sign in
            </Button>
          </>
        )}

        {state === 'form' && (
          <>
            <div className="lx-eyebrow" style={{ marginBottom: 10 }}>{isReset ? 'Password reset' : 'Welcome to Augment HR'}</div>
            <h1 style={{ fontSize: 30, fontWeight: 800, letterSpacing: '-0.02em', color: '#0B1220', margin: '0 0 8px', fontFamily: "'Plus Jakarta Sans',sans-serif" }}>{title}</h1>
            <p style={{ color: '#667085', fontSize: 15, lineHeight: 1.6, margin: '0 0 24px' }}>
              {info?.name ? <>Hi {info.name}, choose a password for <b>{info.email}</b>.</> : 'Choose a secure password to continue.'}
            </p>
            <form onSubmit={submit}>
              <TextField
                fullWidth label="New password" type={show ? 'text' : 'password'}
                value={pw} onChange={(e) => setPw(e.target.value)} autoFocus sx={{ ...inputSx, mb: 2 }}
                InputProps={{ endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShow((v) => !v)} edge="end" size="small">
                      {show ? <EyeOff size={18} /> : <Eye size={18} />}
                    </IconButton>
                  </InputAdornment>
                ) }}
              />
              <TextField
                fullWidth label="Confirm password" type={show ? 'text' : 'password'}
                value={confirm} onChange={(e) => setConfirm(e.target.value)} sx={{ ...inputSx, mb: 2 }}
              />
              {err && <Alert severity="error" sx={{ borderRadius: 2, mb: 2 }}>{err}</Alert>}
              <Button fullWidth type="submit" variant="contained" size="large" disabled={busy} disableElevation sx={btnSx}>
                {busy ? <><CircularProgress size={18} color="inherit" sx={{ mr: 1 }} /> Saving…</> : title}
              </Button>
            </form>
            <p style={{ color: '#9DA4AE', fontSize: 12.5, marginTop: 16, textAlign: 'center' }}>
              Must be at least 8 characters.
            </p>
          </>
        )}

        {state === 'done' && (
          <>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
              <CheckCircle2 size={24} color="#10b981" />
              <h1 style={{ fontSize: 22, fontWeight: 800, color: '#0B1220', margin: 0 }}>Password set</h1>
            </div>
            <p style={{ color: '#667085', fontSize: 15, lineHeight: 1.6, marginTop: 0 }}>
              Your password has been saved. You can now sign in to Augment HR.
            </p>
            <Button fullWidth variant="contained" size="large" disableElevation sx={btnSx} onClick={goLogin}>
              Go to sign in
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
